import { Box, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { CpvNode } from "./cpvHierarchy";
import { CpvCategoryTree } from "./CpvCategoryTree";
import { filterCpvTree } from "./cpvSearch";

type Props = {
  query: string;
  tree: CpvNode[];
  selected: Set<string>;
  onToggleSelect: (code: string) => void;
  onlyAdditional?: (code: string) => boolean;
  onEscape?: () => void;
};

export const CpvSearchResults = ({ query, tree, selected, onToggleSelect, onlyAdditional, onEscape }: Props) => {
  const { t } = useTranslation();
  const filtered = useMemo(() => filterCpvTree(tree, query), [tree, query]);
  const expandedIds = useMemo(() => collectAllCodes(filtered), [filtered]);

  if (query.trim() === "") return null;

  if (filtered.length === 0) {
    return (
      <Box
        borderWidth="1px"
        borderColor="smBorder"
        borderRadius="md"
        p="4"
        bg="smBackground"
      >
        <Text color="smMuted">
          {t("No matches for {{query}}", { query }) ?? ""}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="md"
      p="2"
      bg="smBackground"
      maxH="24rem"
      overflowY="auto"
      // Same as the browse panel: the rows are the tab stops, not the box holding them.
      tabIndex={-1}
    >
      <CpvCategoryTree
        nodes={filtered}
        selected={selected}
        expandedIds={expandedIds}
        onToggleSelect={onToggleSelect}
        onToggleExpand={() => undefined}
        onlyAdditional={onlyAdditional}
        onEscape={onEscape}
      />
    </Box>
  );
};

function collectAllCodes(nodes: CpvNode[]): Set<string> {
  const out = new Set<string>();
  const walk = (ns: CpvNode[]): void => {
    for (const n of ns) {
      out.add(n.code);
      walk(n.children);
    }
  };
  walk(nodes);
  return out;
}
